import {Injectable} from '@angular/core';
import {
  AdvancedFilterGroup,
  CheckboxFilterItem,
  DateRangeFilterItem,
  FilterGroup,
  GetAdvancedParams,
  GetResponse,
  LeadIncludeOption,
  PaginationData,
} from '@app/shared';
import {BehaviorSubject, Observable, firstValueFrom} from 'rxjs';
import {OpportunityService, CompanyService} from '@app/core';
import {AuthService} from '../../../core/services/auth.service';
import {GlobalValuesService} from '@app/core';

type OpportunitySortDirection = 'asc' | 'desc';

type ActiveGroupingCriterion = {
  field: string;
  values: any[];
  order: number;
};

export type KanbanGroupingCriterion = {
  field: string;
  label: string;
  order: number;
};

/**
 * Índices `Map<id, entidad>` precomputados de los catálogos globales para
 * resolver cada fila de oportunidad en O(1) dentro de mapOpportunityData.
 */
interface OpportunityLookups {
  usersById: Map<number, any>;
  partnersById: Map<number, any>;
  countriesById: Map<number, any>;
  salesTeamsById: Map<number, any>;
  stagesById: Map<number, any>;
}

@Injectable({
  providedIn: 'root'
})
export class OpportunitiesUtilsService {
  private readonly filterOptionPageSize = 100;

  /** Variables principales **/
  totalOpportunities: any[] = [];
  filteredOpportunities$ = new BehaviorSubject<any[]>([]);

  private readonly defaultSortProperty = 'id';
  private readonly defaultSortDirection: OpportunitySortDirection = 'desc';
  // Tope de filas por lote para agrupación/kanban; evita el brute-load inicial.
  private readonly groupedFetchLimit = 50;

  private currentSortProperty: string = this.defaultSortProperty;
  private currentSortDirection: OpportunitySortDirection = this.defaultSortDirection;
  private currentFilterGroups: FilterGroup[][] = [];
  private currentPage = 1;
  private currentPageSize = 10;
  private currentType: string | undefined = undefined; // 'Opportunity' o 'Lead'
  private lastLoadedType: string | undefined = undefined; // Tipo para el que se cargaron los filtros
  private loadSequence = 0;
  private kanbanGroupingMode = false;
  private kanbanPageMode = false;
  private readonly kanbanPageSize = 50;
  private readonly relationIncludes: LeadIncludeOption[] = ['PartnerParent'];

  // Acumulador de oportunidades RAW (mapeadas, pre-agrupación) del kanban.
  // Permite pintar el primer bloque y completar la vista en background.
  private kanbanRawAccumulator: any[] = [];
  private kanbanAutoLoadPending = false;

  /**
   * Término de búsqueda libre introducido en el searchbar de la tabla.
   * Se inyecta en buildFilterGroups como cláusulas `Contains` (OR) sobre los
   * campos buscables para que el filtrado ocurra en el backend y no sólo
   * sobre la página visible.
   */
  private currentSearchTerm: string = '';

  /**
   * Campos del backend sobre los que se aplica la búsqueda libre (operador `Contains`).
   * Son propiedades planas de Opportunity/Lead que el backend ya acepta como filtro:
   *   - `name` se usa en budgets-utils con `Contains` (ver budgets-utils.service.ts:286).
   *   - `email`, `phone`, `city` están registrados en getAvailableCustomFields() y
   *     el backend los acepta vía los filtros personalizados (línea ~1269 usa Contains).
   */
  private readonly SEARCHABLE_FIELDS: string[] = [
    'name',
    'email',
    'phone',
    'city',
  ];

  /** Getter para obtener el tipo actual **/
  getCurrentType(): string | undefined {
    return this.currentType;
  }

  /**
   * Establece el término de búsqueda libre que se inyectará en la próxima
   * llamada a getOpportunities(). No dispara la petición por sí mismo: el
   * llamador debe invocar getOpportunities({resetPage: true}) a continuación.
   */
  setSearchTerm(term: string): void {
    this.currentSearchTerm = (term || '').trim();
  }

  setKanbanGroupingMode(isKanban: boolean): void {
    this.kanbanGroupingMode = isKanban;

    if (isKanban) {
      this.clearStageGroupingForKanban();
    }
  }

  setKanbanPageMode(isKanban: boolean): void {
    if (this.kanbanPageMode === isKanban) {
      return;
    }

    this.kanbanPageMode = isKanban;
    this.kanbanRawAccumulator = [];
    this.currentPage = 1;
    this.setKanbanGroupingMode(isKanban);

    if (isKanban) {
      this.currentPageSize = this.kanbanPageSize;

      this.paginationData$.next({
        ...this.paginationData$.value,
        pageSize: this.kanbanPageSize,
        currentPage: 1,
        startRow: 0,
        endRow: 0,
      });
    }
  }

  isKanbanGroupingMode(): boolean {
    return this.kanbanGroupingMode;
  }

  private clearStageGroupingForKanban(): void {
    const beforeCount = this.getActiveGroupingCount();
    this.groupByOptions = this.clearGroupsByName(this.groupByOptions, ['Etapa']);
    this.storageGroupByOptions = this.clearGroupsByName(this.storageGroupByOptions, ['Etapa']);
    const afterCount = this.getActiveGroupingCount();

    if (beforeCount !== afterCount) {
      this.activeGrouping$.next(afterCount);
    }
  }

  private clearGroupsByName(groups: AdvancedFilterGroup[], names: string[]): AdvancedFilterGroup[] {
    const normalizedNames = new Set(names.map((name) => this.normalizeText(name)));

    return groups.map((group) => {
      if (!normalizedNames.has(this.normalizeText(group.name))) {
        return group;
      }

      return {
        ...group,
        filtersActives: 0,
        groupingOrder: undefined,
        advancedFiltersItems: group.advancedFiltersItems.map((item) => ({
          ...item,
          isActive: false,
        })),
        shownFiltersItems: group.shownFiltersItems.map((item) => ({
          ...item,
          isActive: false,
        })),
      };
    });
  }

  private clearGroupOptions(groups: AdvancedFilterGroup[]): AdvancedFilterGroup[] {
    return groups.map((group) => ({
      ...group,
      filtersActives: 0,
      advancedFiltersItems: group.advancedFiltersItems.map((item) => ({
        ...item,
        isActive: false,
      })),
      shownFiltersItems: group.shownFiltersItems.map((item) => ({
        ...item,
        isActive: false,
      })),
    }));
  }

  /**
   * Limpia los items cargados de los filtros (excepto filtros personalizados)
   */
  private clearFilterItems(): void {
    this.filters.forEach(filter => {
      // No limpiar el filtro personalizado ni los filtros de fecha
      if (filter.name !== 'Añadir filtro personalizado' &&
          filter.name !== 'Fecha de creación' &&
          filter.name !== 'Fecha de conversión' &&
          filter.name !== 'Cierre previsto' &&
          filter.name !== 'Fecha de cierre') {
        filter.advancedFiltersItems = [];
        filter.shownFiltersItems = [];
        // Mantener los filtros activos si los hay
      }
    });

    this.storageFilters.forEach(filter => {
      // No limpiar el filtro personalizado ni los filtros de fecha
      if (filter.name !== 'Añadir filtro personalizado' &&
          filter.name !== 'Fecha de creación' &&
          filter.name !== 'Fecha de conversión' &&
          filter.name !== 'Cierre previsto' &&
          filter.name !== 'Fecha de cierre') {
        filter.advancedFiltersItems = [];
        filter.shownFiltersItems = [];
      }
    });
  }

  async loadFilterGroupOptions(groupName: string): Promise<void> {
    switch (groupName) {
      case 'Mi flujo':
        await this.loadMyFlowOpportunities();
        break;
      case 'Creadas por mi':
        await this.loadCreatedByMeOpportunities();
        break;
      case 'No asignado':
        await this.loadUnassignedOpportunities();
        break;
      case 'Oportunidades abiertas':
        await this.loadOpenOpportunities();
        break;
      case 'Ganado':
        await this.loadWonOpportunities();
        break;
      case 'En curso':
        await this.loadInProgressOpportunities();
        break;
      case 'Perdido':
        await this.loadLostOpportunities();
        break;
    }
  }

  /** Variables auxiliares **/
  tableLoading$ = new BehaviorSubject<boolean>(false);

  /** Variables de logica de paginacion y filtrado **/
  paginationData$ = new BehaviorSubject<PaginationData>({
    startRow: 0,
    endRow: 0,
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    pageSize: 10,
  });

  filters: AdvancedFilterGroup[] = [];
  storageFilters: AdvancedFilterGroup[] = [];
  activeFilters$ = new BehaviorSubject<number>(0);

  groupByOptions: AdvancedFilterGroup[] = [];
  storageGroupByOptions: AdvancedFilterGroup[] = [];
  activeGrouping$ = new BehaviorSubject<number>(0);

  // Variables para agrupamiento activo
  private activeGroupByField: string | null = null; // Campo por el que se agrupa (ej.: 'userId', 'companyId')
  private activeGroupByValues: any[] = []; // Valores seleccionados para filtrar

  constructor(
    private opportunityService: OpportunityService,
    private authService: AuthService,
    private globalValuesService: GlobalValuesService,
    private companyService: CompanyService
  ) {
    this.initializeFilters();
    this.initializeGroupByOptions();
    // Los filtros con opciones remotas se cargan bajo demanda desde el drawer.
    // La tabla no necesita precargar esos checkbox para renderizar.
  }

  /**
   * Carga las oportunidades de "Mi flujo": asignadas al usuario autenticado (userId = comercial).
   * Si el backend expone un campo "creado por" (p. ej. createUid), se podría añadir para incluir también las creadas por el usuario.
   */
  private async loadMyFlowOpportunities(): Promise<void> {
    const loggedUser = this.authService.loggedUser$.value;
    if (!loggedUser || !loggedUser.id) {
      return;
    }

    const miFlujoGroup = this.filters.find(f => f.name === 'Mi flujo');
    if (!miFlujoGroup) {
      return;
    }

    try {
      const params: GetAdvancedParams = {
        startRow: 0,
        endRow: this.filterOptionPageSize,
        sortProperty: 'id',
        sortDirection: 'desc',
        includes: [],
        filterGroups: [[{
          property: 'userId',
          operator: 'Equals',
          value: loggedUser.id
        }]]
      };

      const opportunities = await this.loadPagedFilterOptionRows(params);

      // Convertir oportunidades en CheckboxFilterItem
      const checkboxItems = this.toOpportunityCheckboxItems(opportunities);

      // Actualizar el grupo de filtros "Mi flujo"
      miFlujoGroup.advancedFiltersItems = checkboxItems;
      miFlujoGroup.shownFiltersItems = [...checkboxItems];
      // Actualizar el tipo para el que se cargaron los filtros (undefined se trata como 'Opportunity')
      this.lastLoadedType = this.currentType || 'Opportunity';
    } catch (error) {
      console.error('Error al cargar oportunidades de Mi flujo:', error);
    }
  }

  /**
   * Carga las oportunidades "Creadas por mi": filtradas por createdBy = email del usuario autenticado.
   * El backend devuelve createdBy como email (ej.: "david@kameleonlabs.ai").
   */
  private async loadCreatedByMeOpportunities(): Promise<void> {
    const loggedUser = this.authService.loggedUser$.value;
    if (!loggedUser || !loggedUser.email) {
      return;
    }

    const creadasPorMiGroup = this.filters.find(f => f.name === 'Creadas por mi');
    if (!creadasPorMiGroup) {
      return;
    }

    try {
      const params: GetAdvancedParams = {
        startRow: 0,
        endRow: this.filterOptionPageSize,
        sortProperty: 'id',
        sortDirection: 'desc',
        includes: [],
        filterGroups: [[{
          property: 'createdBy',
          operator: 'Equals',
          value: loggedUser.email
        }]]
      };

      const opportunities = await this.loadPagedFilterOptionRows(params);

      const checkboxItems = this.toOpportunityCheckboxItems(opportunities);

      creadasPorMiGroup.advancedFiltersItems = checkboxItems;
      creadasPorMiGroup.shownFiltersItems = [...checkboxItems];
      this.lastLoadedType = this.currentType || 'Opportunity';
    } catch (error) {
      console.error('Error al cargar oportunidades Creadas por mi:', error);
    }
  }

  private async loadUnassignedOpportunities(): Promise<void> {
    const noAsignadoGroup = this.filters.find(f => f.name === 'No asignado');
    if (!noAsignadoGroup) {
      return;
    }

    try {
      const params: GetAdvancedParams = {
        startRow: 0,
        endRow: this.filterOptionPageSize,
        sortProperty: 'id',
        sortDirection: 'desc',
        includes: [],
        filterGroups: [[{
          property: 'companyId',
          operator: 'Equals',
          value: null
        }]]
      };

      const opportunities = await this.loadPagedFilterOptionRows(params);

      // Convertir oportunidades en CheckboxFilterItem
      const checkboxItems = this.toOpportunityCheckboxItems(opportunities);

      // Actualizar el grupo de filtros "No asignado"
      noAsignadoGroup.advancedFiltersItems = checkboxItems;
      noAsignadoGroup.shownFiltersItems = [...checkboxItems];
      // Actualizar el tipo para el que se cargaron los filtros
      this.lastLoadedType = this.currentType;
    } catch (error) {
      console.error('Error al cargar oportunidades no asignadas:', error);
    }
  }

  private async loadOpenOpportunities(): Promise<void> {
    await this.loadInProgressStageOptions('Oportunidades abiertas', 'Error al cargar oportunidades/leads abiertos:');
  }

  private async loadWonOpportunities(): Promise<void> {
    const stageIds = this.getStageIdsByCompletion(true);
    if (!stageIds.length) {
      this.setFilterGroupOptions('Ganado', []);
      return;
    }

    const filterGroups = this.buildStageFilterGroups(stageIds);
    await this.loadStatusFilterOptions('Ganado', filterGroups, 'Error al cargar oportunidades/leads ganados:');
  }

  private async loadInProgressOpportunities(): Promise<void> {
    await this.loadInProgressStageOptions('En curso', 'Error al cargar oportunidades/leads en curso:');
  }

  private async loadLostOpportunities(): Promise<void> {
    await this.loadStatusFilterOptions(
      'Perdido',
      [[{
        property: 'dateLost',
        operator: 'NotEquals',
        value: null,
      }]],
      'Error al cargar oportunidades/leads perdidos:'
    );
  }

  private async loadInProgressStageOptions(groupName: string, errorMessage: string): Promise<void> {
    const stageIds = this.getStageIdsByCompletion(false);
    if (!stageIds.length) {
      this.setFilterGroupOptions(groupName, []);
      return;
    }

    const filterGroups = this.buildStageFilterGroups(stageIds, [{
      property: 'dateLost',
      operator: 'Equals',
      value: null,
    }]);
    await this.loadStatusFilterOptions(groupName, filterGroups, errorMessage);
  }

  private getStageIdsByCompletion(completed: boolean): number[] {
    return this.globalValuesService.stages$.value
      .filter((stage: any) => stage.completed === completed)
      .map((stage: any) => Number(stage.id))
      .filter((stageId: number) => Number.isFinite(stageId));
  }

  private buildStageFilterGroups(stageIds: number[], additionalConditions: FilterGroup[] = []): FilterGroup[][] {
    return [[
      {
        property: 'stageId',
        operator: stageIds.length === 1 ? 'Equals' : 'In',
        value: stageIds.length === 1 ? stageIds[0] : stageIds,
      },
      ...additionalConditions.map((condition) => this.cloneCondition(condition)),
    ]];
  }

  private setFilterGroupOptions(groupName: string, checkboxItems: CheckboxFilterItem[]): void {
    const group = this.filters.find(f => f.name === groupName);
    if (!group) {
      return;
    }

    group.advancedFiltersItems = checkboxItems;
    group.shownFiltersItems = [...checkboxItems];
    this.lastLoadedType = this.currentType;
  }

  private async loadStatusFilterOptions(
    groupName: string,
    filterGroups: FilterGroup[][],
    errorMessage: string
  ): Promise<void> {
    const group = this.filters.find(f => f.name === groupName);
    if (!group) {
      return;
    }

    try {
      const opportunities = await this.loadFilterOptionRows(filterGroups);
      const checkboxItems = this.toOpportunityCheckboxItems(opportunities);

      this.setFilterGroupOptions(groupName, checkboxItems);
    } catch (error) {
      console.error(errorMessage, error);
    }
  }

  private async loadFilterOptionRows(filterGroups: FilterGroup[][]): Promise<any[]> {
    const params: GetAdvancedParams = {
      startRow: 0,
      endRow: this.filterOptionPageSize,
      sortProperty: 'id',
      sortDirection: 'desc',
      includes: [],
      filterGroups: filterGroups.length ? filterGroups : undefined,
    };

    return this.loadPagedFilterOptionRows(params);
  }

  private async loadPagedFilterOptionRows(baseParams: GetAdvancedParams): Promise<any[]> {
    const rows: any[] = [];
    let startRow = 0;
    let totalRows: number | undefined;
    const isLead = this.currentType === 'Lead';

    do {
      const params: GetAdvancedParams = {
        ...baseParams,
        startRow,
        endRow: startRow + this.filterOptionPageSize,
      };

      const response: GetResponse = await firstValueFrom(
        isLead
          ? this.opportunityService.getAdvancedLead(params)
          : this.opportunityService.getAdvancedOpportunity(params)
      );
      const pageRows = response.data?.rows || [];
      rows.push(...pageRows);
      totalRows = response.data?.lastRow;

      if (pageRows.length < this.filterOptionPageSize) {
        break;
      }

      startRow += this.filterOptionPageSize;
    } while (totalRows === undefined || startRow < totalRows);

    return rows;
  }

  private toOpportunityCheckboxItems(opportunities: any[]): CheckboxFilterItem[] {
    return opportunities.map((opp: any) => ({
      label: opp.name || opp.oportunidad || '-',
      value: opp.id,
      isActive: false,
      type: 'checkbox',
      path: 'id',
    }));
  }

  getAvailableCustomFields() {
    return [
      { property: 'email', label: 'Email', path: 'email' },
      { property: 'phone', label: 'Teléfono', path: 'phone' },
      { property: 'website', label: 'Sitio web', path: 'website' },
      { property: 'city', label: 'Ciudad', path: 'city' },
      { property: 'zip', label: 'Código postal', path: 'zip' },
    ];
  }


  private initializeFilters(): void {
    // Inicializar filtros basados en la imagen proporcionada
    this.filters = [
      {
        name: 'Mi flujo',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Creadas por mi',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'No asignado',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Oportunidades abiertas',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Ganado',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'En curso',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Perdido',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Añadir filtro personalizado',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Fecha de creación',
        advancedFiltersItems: [
          {
            label: 'Fecha de creación',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'createdOn'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Fecha de creación',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'createdOn'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      },
      {
        name: 'Fecha de conversión',
        advancedFiltersItems: [
          {
            label: 'Fecha de conversión',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'conversionDate'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Fecha de conversión',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'conversionDate'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      },
      {
        name: 'Cierre previsto',
        advancedFiltersItems: [
          {
            label: 'Cierre previsto',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'expectedCloseDate'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Cierre previsto',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'expectedCloseDate'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      },
      {
        name: 'Fecha de cierre',
        advancedFiltersItems: [
          {
            label: 'Fecha de cierre',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'dateLost'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Fecha de cierre',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'dateLost'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      }
    ];

    // Inicializar storageFilters con una copia profunda
    this.storageFilters = this.filters.map(f => ({
      ...f,
      advancedFiltersItems: f.advancedFiltersItems.map(item => ({ ...item })),
      shownFiltersItems: f.shownFiltersItems.map(item => ({ ...item }))
    }));
  }

  applyFilters(): void {
    // Actualizar los filtros del servicio con los filtros del drawer
    this.filters = [...this.storageFilters];

    // Calcular el número de filtros activos
    let activeCount = 0;
    this.filters.forEach(filterGroup => {
      activeCount += filterGroup.filtersActives;
    });
    this.activeFilters$.next(activeCount);

    // Recargar las oportunidades con los nuevos filtros (mantener el tipo actual)
    void this.getOpportunities({resetPage: true, type: this.currentType});
  }

  resetFilter(): void {
    this.filters.forEach(filterGroup => {
      filterGroup.filtersActives = 0;
      filterGroup.advancedFiltersItems.forEach(item => {
        item.isActive = false;
        if (item.type === 'dateRange') {
          (item as DateRangeFilterItem).value = null;
        }
      });
      filterGroup.shownFiltersItems = [...filterGroup.advancedFiltersItems];
    });
    this.activeFilters$.next(0);
  }

  private initializeGroupByOptions(): void {
    this.groupByOptions = [
      {
        name: 'Comercial',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Equipo de ventas',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Etapa',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Ciudad',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'País',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Razón de pérdida',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Compañía',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Contacto',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      },
      {
        name: 'Fecha de creación',
        advancedFiltersItems: [
          {
            label: 'Fecha de creación',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'createdOn'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Fecha de creación',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'createdOn'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      },
      {
        name: 'Fecha de conversión',
        advancedFiltersItems: [
          {
            label: 'Fecha de conversión',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'conversionDate'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Fecha de conversión',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'conversionDate'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      },
      {
        name: 'Cierre previsto',
        advancedFiltersItems: [
          {
            label: 'Cierre previsto',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'expectedCloseDate'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Cierre previsto',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'expectedCloseDate'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      },
      {
        name: 'Fecha de cierre',
        advancedFiltersItems: [
          {
            label: 'Fecha de cierre',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'dateLost'
          } as DateRangeFilterItem
        ],
        shownFiltersItems: [
          {
            label: 'Fecha de cierre',
            value: null,
            isActive: false,
            type: 'dateRange',
            path: 'dateLost'
          } as DateRangeFilterItem
        ],
        filtersActives: 0
      },
      {
        name: 'Añadir grupo personalizado',
        advancedFiltersItems: [],
        shownFiltersItems: [],
        filtersActives: 0
      }
    ];

    // Inicializar storageGroupByOptions con una copia profunda
    this.storageGroupByOptions = this.groupByOptions.map(g => ({
      ...g,
      advancedFiltersItems: g.advancedFiltersItems.map(item => ({ ...item })),
      shownFiltersItems: g.shownFiltersItems.map(item => ({ ...item }))
    }));
  }

  applyGrouping(): void {
    if (this.kanbanGroupingMode) {
      this.clearStageGroupingForKanban();
    }

    const activeCriteria = this.getActiveGroupingCriteria();
    const primaryCriterion = activeCriteria[0];

    if (primaryCriterion) {
      this.activeGroupByField = primaryCriterion.field;
      this.activeGroupByValues = primaryCriterion.values;
    } else {
      // If there is no explicit group, keep the visual company grouping.
      // That default grouping is also paginated as grouped data in getOpportunities().
      this.activeGroupByField = null;
      this.activeGroupByValues = [];
    }

    const activeCount = this.getActiveGroupingCount();
    this.activeGrouping$.next(activeCount);

    // Recargar las oportunidades con el nuevo agrupamiento (mantener el tipo actual)
    void this.getOpportunities({resetPage: true, type: this.currentType});
  }

  getKanbanGroupingCriteria(): KanbanGroupingCriterion[] {
    return this.groupByOptions
      .map((group, index) => ({group, index}))
      .filter(({group}) => group.filtersActives > 0)
      .filter(({group}) => this.normalizeText(group.name) !== 'etapa')
      .map(({group, index}) => {
        const selectedItems = group.advancedFiltersItems.filter(
          (item) => item.isActive,
        );
        const field = (selectedItems[0] as any)?.path || this.resolveGroupByField(group.name);
        const order = group.groupingOrder ?? Number.MAX_SAFE_INTEGER + index;

        return field ? {field, label: group.name, order} : null;
      })
      .filter((criteria): criteria is KanbanGroupingCriterion => !!criteria)
      .sort((a, b) => a.order - b.order);
  }

  private getActiveGroupingCriteria(): ActiveGroupingCriterion[] {
    return this.groupByOptions
      .map((group, index) => ({group, index}))
      .filter(({group}) => group.filtersActives > 0)
      .filter(({group}) => !this.kanbanGroupingMode || this.normalizeText(group.name) !== 'etapa')
      .map(({group, index}) => {
        const selectedItems = group.advancedFiltersItems.filter(
          (item) => item.isActive,
        );
        const field = (selectedItems[0] as any)?.path || this.resolveGroupByField(group.name);
        const values = selectedItems
          .map((item) => item.value)
          .filter((value) => value !== undefined && value !== null && value !== '');
        const order = group.groupingOrder ?? Number.MAX_SAFE_INTEGER + index;

        return field && values.length > 0 ? {field, values, order} : null;
      })
      .filter((criteria): criteria is ActiveGroupingCriterion => !!criteria)
      .sort((a, b) => a.order - b.order);
  }

  private resolveGroupByField(groupName: string): string | null {
    const normalizedName = this.normalizeText(groupName);

    if (normalizedName === 'comercial') return 'userId';
    if (normalizedName === 'compania') return 'companyId';
    if (normalizedName === 'etapa') return 'stageId';
    if (normalizedName === 'ciudad') return 'city';
    if (normalizedName === 'pais') return 'countryId';
    if (normalizedName === 'contacto') return 'partnerId';
    if (normalizedName === 'equipo de ventas') return 'teamId';
    if (normalizedName === 'razon de perdida') return 'lostReasonId';
    if (normalizedName === 'fecha de creacion') return 'createdOn';
    if (normalizedName === 'fecha de conversion') return 'conversionDate';
    if (normalizedName === 'cierre previsto') return 'expectedCloseDate';
    if (normalizedName === 'fecha de cierre') return 'dateLost';

    return null;
  }

  private normalizeText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private buildGroupingFilterConditions(): FilterGroup[] {
    const conditions: FilterGroup[] = [];

    this.getActiveGroupingCriteria().forEach((criteria) => {
      if (this.isDateGroupingField(criteria.field)) {
        criteria.values.forEach((dateRange) => {
          if (Array.isArray(dateRange) && dateRange.length === 2) {
            const startDate = this.toStartOfDayFilterDate(dateRange[0]);
            const endDate = this.toEndOfDayFilterDate(dateRange[1]);

            if (startDate) {
              conditions.push({
                property: criteria.field,
                operator: 'GreaterThanOrEqual',
                value: startDate,
              });
            }

            if (endDate) {
              conditions.push({
                property: criteria.field,
                operator: 'LessThanOrEqual',
                value: endDate,
              });
            }
          }
        });
        return;
      }

      if (criteria.values.length === 1) {
        conditions.push({
          property: criteria.field,
          operator: 'Equals',
          value: this.toFilterValue(criteria.values[0]),
        });
      } else {
        conditions.push({
          property: criteria.field,
          operator: 'In',
          value: criteria.values.map((v) => this.toFilterValue(v)) as Array<
            string | number | boolean | null
          >,
        });
      }
    });

    return conditions;
  }

  private isDateGroupingField(field: string): boolean {
    return field.includes('Date') || field.includes('On');
  }

  private toStartOfDayFilterDate(value: any): string | null {
    const date = this.toLocalFilterDate(value);
    return date ? `${date}T00:00:00.000` : null;
  }

  private toEndOfDayFilterDate(value: any): string | null {
    const date = this.toLocalFilterDate(value);
    return date ? `${date}T23:59:59.999` : null;
  }

  private toLocalFilterDate(value: any): string | null {
    if (!value) return null;
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = `${value.getMonth() + 1}`.padStart(2, '0');
      const day = `${value.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return String(value).split('T')[0];
  }

  /** Métodos para carga de datos **/
  async getOpportunities(options: {
    resetPage?: boolean;
    page?: number;
    skipPageAdjustment?: boolean;
    type?: string; // 'Opportunity' o 'Lead'
    append?: boolean;
    background?: boolean;
  } = {}): Promise<void> {
    const requestId = ++this.loadSequence;
    const {resetPage, page, skipPageAdjustment, type, append, background = false} = options;

    // Guardar el tipo actual para usar en llamadas internas
    const previousType = this.lastLoadedType;
    const newType = type !== undefined ? type : this.currentType;

    if (type !== undefined) {
      this.currentType = type;
    }

    // Normalizar tipos para comparación (undefined se trata como 'Opportunity')
    const normalizedNewType = newType || 'Opportunity';
    const normalizedPreviousType = previousType || 'Opportunity';

    // Si el tipo cambió, limpiar los items de los filtros y recargarlos
    const typeChanged = normalizedNewType !== normalizedPreviousType;
    if (!background) {
      this.tableLoading$.next(true);
    }

    if (typeChanged) {
      this.totalOpportunities = [];
      this.filteredOpportunities$.next([]);
      this.clearFilterItems();
      // Limpiar también el término de búsqueda libre al cambiar entre
      // Opportunity y Lead para evitar que un término "colgado" devuelva
      // resultados inesperados en la nueva vista.
      this.currentSearchTerm = '';
      this.lastLoadedType = normalizedNewType;
    }

    if (resetPage) {
      this.currentPage = 1;
    }

    if (page !== undefined) {
      this.currentPage = Math.max(1, page);
    }

    const pageSize = this.currentPageSize;
    const startRow = (this.currentPage - 1) * pageSize;
    const endRow = startRow + pageSize;
    // The opportunities table is always rendered as grouped data:
    // - by company when there is no explicit grouping;
    // - by the selected criteria when the user applies grouping.
    //
    // Therefore we must not backend-page opportunities before grouping them.
    // Fetch a broad result set first, group it, then paginate group rows.
    const isGroupedMode = this.isGroupedPresentationActive();
    const useGroupedBulkFetch = isGroupedMode && !this.kanbanPageMode;

    // Solo construir filtros por tipo si es una oportunidad (no necesario para leads ya que el endpoint es específico)
    const filterGroups = type === 'Lead' ? this.buildFilterGroups() : this.buildFilterGroups(type);
    this.currentFilterGroups = filterGroups;

    const params: GetAdvancedParams = {
      startRow: useGroupedBulkFetch ? 0 : startRow,
      endRow: useGroupedBulkFetch ? this.groupedFetchLimit : endRow,
      sortProperty: this.currentSortProperty,
      sortDirection: this.currentSortDirection,
      filterGroups: filterGroups.length ? filterGroups : undefined,
      includes: this.relationIncludes,
    };

    try {
      const effectiveType = type !== undefined ? type : this.currentType;
      const selectedCompanyIds = this.companyService.selectedCompanyIds;

      // Índices de catálogos construidos una sola vez para mapear el lote en O(1).
      const lookups = this.buildOpportunityLookups();

      // El endpoint advanced acepta `companyId` como ARRAY en el payload,
      // así que UNA sola petición cubre todas las compañías seleccionadas.
      //
      // Antes esto hacía un forkJoin de 1 petición POR compañía (N requests
      // donde basta 1) y calculaba totalItems como "máximo por compañía",
      // un hack de paginación necesario solo porque se mergeaban N
      // result-sets paginados por separado. Con la llamada única, `lastRow`
      // es el total real combinado — más correcto y más rápido.
      let opportunities: any[] = [];
      let totalItems = 0;

      const companyIdsParam =
        selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined;
      const response = await this.fetchAdvancedRows(
        effectiveType,
        params,
        useGroupedBulkFetch,
        companyIdsParam,
        lookups,
      );
      opportunities = response.rows;
      totalItems = response.totalItems;

      // En kanban se acumulan lotes para pintar rápido y completar en background.
      const shouldAccumulateKanban = this.kanbanPageMode;
      if (shouldAccumulateKanban && append) {
        this.kanbanRawAccumulator = [...this.kanbanRawAccumulator, ...opportunities];
      } else if (shouldAccumulateKanban) {
        this.kanbanRawAccumulator = opportunities;
      } else {
        this.kanbanRawAccumulator = [];
      }

      // Agrupar el acumulador completo (en no-append == solo esta página).
      const rows = this.groupOpportunities(
        shouldAccumulateKanban ? this.kanbanRawAccumulator : opportunities,
      );

      if (!this.isLatestLoad(requestId)) {
        return;
      }

      if (isGroupedMode) {
        if (this.isDefaultCompanyGroupingActive()) {
          const companyPage = this.paginateDefaultCompanyGroups(rows, pageSize);

          if (!background) {
            this.tableLoading$.next(false);
          }
          this.totalOpportunities = companyPage.rows;
          this.filteredOpportunities$.next(companyPage.rows);

          this.paginationData$.next({
            startRow: companyPage.startRow,
            endRow: companyPage.endRow,
            currentPage: this.currentPage,
            totalPages: companyPage.totalPages,
            totalItems: companyPage.totalItems,
            pageSize,
          });

          this.setActiveFilters();
          this.queueNextKanbanPage(totalItems, opportunities.length);
          return;
        }

        const groupedTotalItems = rows.length;
        const groupedPageSize = this.isStageGroupingActive()
          ? Math.max(groupedTotalItems, 1)
          : pageSize;
        const groupedTotalPages = groupedPageSize > 0 ? Math.ceil(groupedTotalItems / groupedPageSize) : 0;
        const effectiveGroupedTotalPages =
          groupedTotalPages > 0 ? groupedTotalPages : (groupedTotalItems > 0 ? 1 : 0);

        if (effectiveGroupedTotalPages > 0 && this.currentPage > effectiveGroupedTotalPages) {
          this.currentPage = effectiveGroupedTotalPages;
        }

        if (groupedTotalItems === 0) {
          this.currentPage = 1;
        }

        const groupedStartRow = this.kanbanPageMode
          ? 0
          : (groupedTotalItems > 0 ? (this.currentPage - 1) * groupedPageSize : 0);
        const groupedEndRow = this.kanbanPageMode
          ? groupedTotalItems
          : (groupedTotalItems > 0 ? Math.min(groupedStartRow + groupedPageSize, groupedTotalItems) : 0);
        const pagedGroupedRows = this.kanbanPageMode
          ? rows
          : rows.slice(groupedStartRow, groupedEndRow);

        if (!background) {
          this.tableLoading$.next(false);
        }
        this.totalOpportunities = pagedGroupedRows;
        this.filteredOpportunities$.next(pagedGroupedRows);

        this.paginationData$.next({
          startRow: groupedStartRow,
          endRow: groupedEndRow,
          currentPage: this.currentPage,
          totalPages: groupedTotalPages,
          totalItems: groupedTotalItems,
          pageSize: groupedPageSize,
        });

        this.setActiveFilters();
        this.queueNextKanbanPage(totalItems, opportunities.length);
        return;
      }

      const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
      const effectiveTotalPages = totalPages > 0 ? totalPages : (totalItems > 0 ? 1 : 0);

      if (!skipPageAdjustment && totalItems > 0 && rows.length === 0 && this.currentPage > 1) {
        const targetPage = effectiveTotalPages > 0 ? effectiveTotalPages : 1;
        this.currentPage = targetPage;
        await this.getOpportunities({
          page: targetPage,
          skipPageAdjustment: true,
          type: type !== undefined ? type : this.currentType
        });
        return;
      }

      if (!background) {
        this.tableLoading$.next(false);
      }
      this.totalOpportunities = rows;
      this.filteredOpportunities$.next(rows);

      if (effectiveTotalPages > 0 && this.currentPage > effectiveTotalPages) {
        this.currentPage = effectiveTotalPages;
      }

      if (totalItems === 0) {
        this.currentPage = 1;
      }

      const computedStartRow = totalItems > 0 ? (this.currentPage - 1) * pageSize : 0;
      const computedEndRow = totalItems > 0 ? Math.min(computedStartRow + pageSize, totalItems) : 0;

      this.paginationData$.next({
        startRow: computedStartRow,
        endRow: computedEndRow,
        currentPage: this.currentPage,
        totalPages,
        totalItems,
        pageSize,
      });

      this.setActiveFilters();
      this.queueNextKanbanPage(totalItems, opportunities.length);
    } catch (e) {
      console.error('Error al obtener Oportunidades:', e);
      if (!background && this.isLatestLoad(requestId)) {
        this.tableLoading$.next(false);
      }
    }
  }

  private getAdvancedRowsObservable(
    effectiveType: string | undefined,
    params: GetAdvancedParams,
    companyIds?: number[],
  ): Observable<GetResponse> {
    return effectiveType === 'Lead'
      ? this.opportunityService.getAdvancedLead(params, false, companyIds)
      : this.opportunityService.getAdvancedOpportunity(params, true, companyIds);
  }

  private async fetchAdvancedRows(
    effectiveType: string | undefined,
    params: GetAdvancedParams,
    fetchAllPages: boolean,
    companyIds?: number[],
    lookups?: OpportunityLookups,
  ): Promise<{rows: any[]; totalItems: number}> {
    if (!fetchAllPages) {
      const response = await firstValueFrom(
        this.getAdvancedRowsObservable(effectiveType, params, companyIds),
      );
      const rawRows = response.data?.rows || [];

      return {
        rows: rawRows.map((row: any) => this.mapOpportunityData(row, lookups)),
        totalItems: response.data?.lastRow ?? rawRows.length,
      };
    }

    const chunkSize = Math.max(
      1,
      (params.endRow ?? this.groupedFetchLimit) - (params.startRow ?? 0),
    );
    let nextStartRow = 0;
    let totalItems = 0;
    const rows: any[] = [];

    while (true) {
      const response = await firstValueFrom(
        this.getAdvancedRowsObservable(
          effectiveType,
          {
            ...params,
            startRow: nextStartRow,
            endRow: nextStartRow + chunkSize,
          },
          companyIds,
        ),
      );
      const rawRows = response.data?.rows || [];
      rows.push(...rawRows.map((row: any) => this.mapOpportunityData(row, lookups)));
      totalItems = response.data?.lastRow ?? rows.length;

      if (
        rawRows.length === 0 ||
        rows.length >= totalItems ||
        rawRows.length < chunkSize
      ) {
        break;
      }

      nextStartRow += chunkSize;
    }

    return {rows, totalItems};
  }

  private isLatestLoad(requestId: number): boolean {
    return requestId === this.loadSequence;
  }

  private buildFilterGroups(type?: string): FilterGroup[][] {
    const baseConditions: FilterGroup[] = [];
    const filterGroups: FilterGroup[][] = [];

    // 1) Condiciones base (AND global): tipo, agrupacion activa, filtros personalizados y fechas.
    if (type && type !== 'Lead') {
      baseConditions.push({
        property: 'type',
        operator: 'Equals',
        value: type,
      });
    }

    // Agrupacion activa: todos los criterios seleccionados se envian como filtros AND.
    baseConditions.push(...this.buildGroupingFilterConditions());

    // Filtro "Añadir filtro personalizado": siempre en AND global
    const customGroup = this.filters.find(
      (fg) =>
        fg.name === 'Añadir filtro personalizado' && fg.filtersActives > 0
    );

    if (customGroup) {
      const activeCustomItems = customGroup.advancedFiltersItems.filter(
        (item) => item.isActive && item.type === 'checkbox'
      ) as CheckboxFilterItem[];

      activeCustomItems.forEach((item) => {
        // Ignorar las definiciones de campo (no valores)
        if (
          this.getAvailableCustomFields().some(
            (f) => f.property === item.value
          )
        ) {
          return;
        }

        baseConditions.push({
          property: item.path,
          operator: 'Contains',
          value: this.toFilterValue(item.value),
        });
      });
    }

    // Filtros de fecha (Fecha de creación, conversión, cierre...):
    // aplicar SIEMPRE en AND global, independientemente del grupo rápido.
    this.filters.forEach((fg) => {
      fg.advancedFiltersItems.forEach((item) => {
        if (item.type === 'dateRange' && item.value && item.value.length === 2) {
          const dateRange = item.value;
          const alreadyProcessed = baseConditions.some(
            (condition) =>
              condition.property === item.path &&
              (condition.operator === 'GreaterThanOrEqual' ||
                condition.operator === 'LessThanOrEqual')
          );

          if (!alreadyProcessed) {
            const startDate = this.toStartOfDayFilterDate(dateRange[0]);
            const endDate = this.toEndOfDayFilterDate(dateRange[1]);

            if (startDate) {
              baseConditions.push({
                property: item.path,
                operator: 'GreaterThanOrEqual',
                value: startDate,
              });
            }

            if (endDate) {
              baseConditions.push({
                property: item.path,
                operator: 'LessThanOrEqual',
                value: endDate,
              });
            }
          }
        }
      });
    });

    // 2) Grupos rápidos (Mi flujo, No asignado, Oportunidades abiertas, Ganado, etc.)
    //    Dentro de cada grupo: AND entre sus elementos.
    //    Entre grupos: OR.
    // ---------------------------------------------------------------
    const dateFilterNames = [
      'Fecha de creación',
      'Fecha de conversión',
      'Cierre previsto',
      'Fecha de cierre',
    ];

    this.filters
      .filter(
        (fg) =>
          fg.filtersActives > 0 &&
          fg.name !== 'Añadir filtro personalizado' &&
          !dateFilterNames.includes(fg.name)
      )
      .forEach((fg) => {
        const activeItems = fg.advancedFiltersItems.filter(
          (item) => item.isActive
        );
        if (!activeItems.length) {
          return;
        }

        const groupConditions: FilterGroup[] = [];

        // 2.a) Checkboxes: combinar todos los valores del grupo en un único 'In' (AND dentro del grupo)
        // Formato: { property, value: [...], operator: 'In'} â€” elementos del grupo con AND, grupos con OR
        const checkboxItems = activeItems.filter(
          (item) => item.type === 'checkbox'
        ) as CheckboxFilterItem[];

        if (checkboxItems.length) {
          const path = checkboxItems[0].path;
          const values = checkboxItems
            .map((item) => item.value)
            .filter((v) => v !== undefined && v !== null);
          const normalizedValues = values.map((v) =>
            this.toFilterValue(v)
          ) as Array<string | number | boolean | null>;

          if (normalizedValues.length) {
            groupConditions.push({
              property: path,
              operator: 'In',
              value: normalizedValues,
            });
          }
        }

        // 2.b) Items de tipo 'event': agrupar todos los ids en un único 'In' (AND dentro del grupo)
        const eventItems = activeItems.filter(
          (item) => item.type === 'event'
        ) as any[];

        if (eventItems.length) {
          const allIds: number[] = [];
          eventItems.forEach((item) => {
            const ids = (item as any).idGroup as number[] | undefined;
            if (ids && ids.length) {
              allIds.push(...ids);
            }
          });

          if (allIds.length) {
            const normalizedIds = allIds
              .map((id) => this.toFilterValue(id))
              .filter((v): v is number => typeof v === 'number') as number[];
            if (normalizedIds.length) {
              groupConditions.push({
                property: 'State',
                operator: 'In',
                value: normalizedIds,
              });
            }
          }
        }

        if (!groupConditions.length) {
          return;
        }

        // Cada grupo rápido genera un "AND" interno y luego se combina con las
        // condiciones base también en AND. Resultado final:
        //   (grupo1 AND base) OR (grupo2 AND base) OR ...
        const combinedGroup: FilterGroup[] = [
          ...groupConditions.map((c) => this.cloneCondition(c)),
          ...baseConditions.map((c) => this.cloneCondition(c)),
        ];

        filterGroups.push(combinedGroup);
      });

    // Si no hay grupos rápidos activos, pero sí condiciones base,
    // devolvemos solo las condiciones base en un único grupo AND.
    // Excepción: si la única condición base es "type", no enviar filterGroups
    // (el endpoint ya filtra por tipo; así evitamos [[{type, Equals, ... }]]).
    if (!filterGroups.length) {
      const onlyType =
        baseConditions.length === 1 && baseConditions[0].property === 'type';

      if (!baseConditions.length || onlyType) {
        // Sin condiciones base relevantes: si hay término de búsqueda libre
        // devolvemos un grupo OR por cada campo buscable; si no, vacío.
        return this.applySearchTermToFilterGroups([], baseConditions);
      }

      const baseGroup: FilterGroup[][] = [
        baseConditions.map((c) => this.cloneCondition(c)),
      ];
      return this.applySearchTermToFilterGroups(baseGroup, baseConditions);
    }

    return this.applySearchTermToFilterGroups(filterGroups, baseConditions);
  }

  /**
   * Inyecta el término de búsqueda libre (`currentSearchTerm`) sobre un conjunto
   * de `filterGroups` ya construido.
   *
   * Semántica resultante: `(F1 OR F2 OR ... OR Fn) AND (filtros existentes)`.
   * Como `FilterGroup[][]` usa outer=OR, inner=AND, expandimos cada grupo
   * existente multiplicándolo por el número de campos buscables, añadiendo una
   * cláusula `Contains` en cada copia.
   *
   * - Si no hay término, devuelve los grupos tal cual.
   * - Si no hay grupos previos, devuelve un grupo por cada campo buscable con
   *   las condiciones base (excepto `type`) incluidas para preservar el AND
   *   global. `type` se omite porque el endpoint ya filtra por tipo
   *   (mismo criterio que el early-return original de buildFilterGroups).
   */
  private applySearchTermToFilterGroups(
    filterGroups: FilterGroup[][],
    baseConditions: FilterGroup[],
  ): FilterGroup[][] {
    const term = this.currentSearchTerm;
    if (!term) {
      return filterGroups;
    }

    const searchClauses: FilterGroup[] = this.SEARCHABLE_FIELDS.map((field) => ({
      property: field,
      operator: 'Contains',
      value: term,
    }));

    // El endpoint ya filtra por type, así que lo omitimos al combinar con las
    // cláusulas de búsqueda para evitar redundancia.
    const relevantBase = baseConditions.filter((c) => c.property !== 'type');

    if (!filterGroups.length) {
      // Sin grupos previos: un grupo OR por cada campo buscable.
      return searchClauses.map((clause) => [
        { ...clause },
        ...relevantBase.map((c) => this.cloneCondition(c)),
      ]);
    }

    // Con grupos previos (rápidos o base): expandir cada grupo por cada
    // cláusula de búsqueda.
    const expanded: FilterGroup[][] = [];
    filterGroups.forEach((group) => {
      searchClauses.forEach((clause) => {
        expanded.push([
          ...group.map((c) => this.cloneCondition(c)),
          { ...clause },
        ]);
      });
    });
    return expanded;
  }

  private createEqualsCondition(property: string, value: any, collectionGroup?: string): FilterGroup {
    const condition: FilterGroup = {
      property,
      value: this.toFilterValue(value),
      operator: 'Equals'
    };

    if (collectionGroup) {
      condition.collectionGroup = collectionGroup;
    }

    return condition;
  }

  private toFilterValue(value: any): string | number | boolean | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return String(value);
  }

  private cloneCondition(condition: FilterGroup): FilterGroup {
    return {
      property: condition.property,
      value: condition.value,
      operator: condition.operator,
      collectionGroup: condition.collectionGroup,
    };
  }

  private isGroupedPresentationActive(): boolean {
    return this.getDisplayGroupingCriteria().length > 0;
  }

  private isDefaultCompanyGroupingActive(): boolean {
    return this.getDisplayGroupingCriteria().length === 0;
  }

  private paginateDefaultCompanyGroups(
    companyRows: any[],
    pageSize: number,
  ): {rows: any[]; startRow: number; endRow: number; totalPages: number; totalItems: number} {
    const sanitizedPageSize = Math.max(1, pageSize);
    const maxItemsPerCompany = companyRows.reduce(
      (max, companyRow) => Math.max(max, companyRow.items?.length || 0),
      0,
    );
    const totalPages = maxItemsPerCompany > 0
      ? Math.ceil(maxItemsPerCompany / sanitizedPageSize)
      : (companyRows.length > 0 ? 1 : 0);
    const effectiveTotalPages = totalPages > 0 ? totalPages : 1;

    if (this.currentPage > effectiveTotalPages) {
      this.currentPage = effectiveTotalPages;
    }

    if (maxItemsPerCompany === 0) {
      this.currentPage = 1;
    }

    const startRow = maxItemsPerCompany > 0
      ? (this.currentPage - 1) * sanitizedPageSize
      : 0;
    const endRow = maxItemsPerCompany > 0
      ? Math.min(startRow + sanitizedPageSize, maxItemsPerCompany)
      : 0;
    const pagedRows = companyRows.map((companyRow) => {
      const allItems = companyRow.items || [];

      return {
        ...companyRow,
        items: allItems.slice(startRow, endRow),
      };
    });

    return {
      rows: pagedRows,
      startRow,
      endRow,
      totalPages,
      totalItems: maxItemsPerCompany,
    };
  }

  private getActiveGroupingCount(): number {
    return this.groupByOptions.reduce(
      (count, group) => count + group.filtersActives,
      0,
    );
  }

  private isStageGroupingActive(): boolean {
    return this.getActiveGroupingCriteria().some(
      (criterion) => criterion.field === 'stageId',
    );
  }

  private setActiveFilters(): void {
    let activeCount = 0;
    this.filters.forEach(filterGroup => {
      activeCount += filterGroup.filtersActives;
    });
    this.activeFilters$.next(activeCount);
  }

  setPageSize(pageSize: number, reload: boolean, forceReload: boolean = false): void {
    const sanitizedPageSize = this.kanbanPageMode
      ? this.kanbanPageSize
      : Math.max(1, pageSize);
    const paginationSnapshot = this.paginationData$.value;
    const paginationAlreadySynced = paginationSnapshot.pageSize === sanitizedPageSize;

    if (!forceReload && this.currentPageSize === sanitizedPageSize && paginationAlreadySynced) {
      return;
    }

    this.currentPageSize = sanitizedPageSize;
    this.currentPage = 1;

    this.paginationData$.next({
      ...paginationSnapshot,
      pageSize: sanitizedPageSize,
      currentPage: 1,
      startRow: 0,
      endRow: 0,
    });

    if (reload) {
      void this.getOpportunities({resetPage: true, type: this.currentType});
    }
  }

  setCurrentPage(page: number): void {
    this.currentPage = Math.max(1, page);
    void this.getOpportunities({
      page: this.currentPage,
      skipPageAdjustment: true,
      type: this.currentType,
    });
  }

  /**
   * Arms the next kanban page. Stops on three conditions, not one: the accumulator
   * reached the reported total, the round brought nothing back, or the round came
   * back short of a full page — which means there is no next page to ask for.
   *
   * Reaching the total was the only stop condition until now, so a total the
   * accumulator could never reach left this rescheduling itself forever at network
   * speed. Budgets hit exactly that; this pager is identical and was equally exposed,
   * it simply had not been triggered.
   *
   * Same three-part shape already used by fetchAdvancedRows below.
   */
  private queueNextKanbanPage(totalItems: number, fetchedThisRound: number): void {
    if (
      !this.kanbanPageMode ||
      this.kanbanAutoLoadPending ||
      this.kanbanRawAccumulator.length >= totalItems
    ) {
      return;
    }

    // No progress, or a short page: there is nothing further to fetch. If the server
    // still reports more rows than we hold, the count and the rows disagree — say so
    // rather than swallowing it, because a silent stop hides a wrong total.
    if (fetchedThisRound === 0 || fetchedThisRound < this.kanbanPageSize) {
      if (this.kanbanRawAccumulator.length < totalItems) {
        console.warn(
          '[opportunities] Kanban pagination stopped short: server reports',
          totalItems,
          'rows but returned',
          this.kanbanRawAccumulator.length,
          '. The reported total and the rows returned disagree.'
        );
      }
      return;
    }

    this.kanbanAutoLoadPending = true;
    setTimeout(() => {
      this.kanbanAutoLoadPending = false;
      if (!this.kanbanPageMode || this.kanbanRawAccumulator.length >= totalItems) {
        return;
      }

      void this.getOpportunities({
        page: this.currentPage + 1,
        skipPageAdjustment: true,
        append: true,
        background: true,
        type: this.currentType,
      });
    });
  }

  updateSort(sortProperty: string, sortDirection: OpportunitySortDirection): void {
    this.currentSortProperty = sortProperty;
    this.currentSortDirection = sortDirection;
    void this.getOpportunities({resetPage: true, type: this.currentType});
  }

  /**
   * Construye índices `Map<id, entidad>` de los catálogos globales (usuarios,
   * partners, países, equipos de ventas y etapas) para que `mapOpportunityData` resuelva
   * cada fila en O(1) en lugar de hacer `.find()` lineal por fila.
   *
   * Debe construirse UNA sola vez antes de mapear un lote de filas y pasarse a
   * `mapOpportunityData`. Para llamadas de una única fila se puede omitir (el
   * coste de un par de `.find()` puntuales es menor que construir los Map).
   */
  private buildOpportunityLookups(): OpportunityLookups {
    const usersById = new Map<number, any>();
    for (const user of this.globalValuesService.users$.value) {
      usersById.set(user.id, user);
    }

    const partnersById = new Map<number, any>();
    for (const partner of this.globalValuesService.partnerlist) {
      partnersById.set(partner.id, partner);
    }

    const countriesById = new Map<number, any>();
    for (const country of this.globalValuesService.countries$.value) {
      countriesById.set(country.id, country);
    }

    const salesTeamsById = new Map<number, any>();
    for (const team of this.globalValuesService.salesTeams$.value) {
      salesTeamsById.set(team.id, team);
    }

    const stagesById = new Map<number, any>();
    for (const stage of this.globalValuesService.stages$.value) {
      stagesById.set(stage.id, stage);
    }

    return { usersById, partnersById, countriesById, salesTeamsById, stagesById };
  }

  /**
   * Mapea los datos del backend a las propiedades que espera la tabla.
   *
   * @param lookups Índices precomputados de catálogos. Cuando se mapea un lote
   *   de filas debe pasarse (ver buildOpportunityLookups) para evitar el coste
   *   O(filas × catálogo) de los `.find()` por fila. Si se omite, se resuelve
   *   con `.find()` lineal (apto solo para una fila suelta).
   */
  mapOpportunityData(opportunity: any, lookups?: OpportunityLookups): any {
    const findUser = (id: number) =>
      lookups ? lookups.usersById.get(id) : this.globalValuesService.users$.value.find(u => u.id === id);
    const findPartner = (id: number) =>
      lookups ? lookups.partnersById.get(id) : this.globalValuesService.partnerlist.find(p => p.id === id);
    const findCountry = (id: number) =>
      lookups ? lookups.countriesById.get(id) : this.globalValuesService.countries$.value.find(c => c.id === id);
    const findSalesTeam = (id: number) =>
      lookups ? lookups.salesTeamsById.get(id) : this.globalValuesService.salesTeams$.value.find(t => t.id === id);
    const findStage = (id: number) =>
      lookups ? lookups.stagesById.get(id) : this.globalValuesService.stages$.value.find(s => s.id === id);
    const getEntityDisplayName = (entity: any): string | null => {
      if (entity == null || entity === '') return null;
      if (typeof entity === 'string') return entity.trim() || null;

      const name = `${entity.name || ''} ${entity.surName || ''}`.trim();
      return name || entity.businessName || entity.displayName || null;
    };

    // Obtener el nombre de la etapa
    let etapa = '-';
    if (opportunity.stage) {
      if (typeof opportunity.stage === 'string') {
        etapa = opportunity.stage;
      } else if (opportunity.stage.name) {
        etapa = opportunity.stage.name;
      }
    } else if (opportunity.stageId) {
      etapa = findStage(opportunity.stageId)?.name || '-';
    }

    // Obtener el comercial/usuario desde userId
    let comercial = '-';
    let comercialUser: any = null;
    if (opportunity.userId) {
      const user = findUser(opportunity.userId);
      if (user && user.partner) {
        const userName = user.partner.name || '';
        const userSurName = user.partner.surName || '';
        comercial = `${userName} ${userSurName}`.trim() || '-';
        comercialUser = user.partner; // Guardar el partner completo para el avatar
      }
    }

    // Resolver el partner del contacto UNA sola vez (se reutiliza para nombre,
    // empresa cliente, ciudad y país).
    const catalogPartner =
      opportunity.partnerId ? findPartner(opportunity.partnerId) : null;
    const contactPartnerEntity =
      opportunity.contact ||
      opportunity.contactPartner ||
      (opportunity.partner && !opportunity.partner.isCompany ? opportunity.partner : null) ||
      (catalogPartner && !catalogPartner.isCompany ? catalogPartner : null);

    // Obtener el contacto desde partnerId
    const contactName = getEntityDisplayName(contactPartnerEntity) || '-';
    const contactPartner =
      contactPartnerEntity && typeof contactPartnerEntity === 'object'
        ? contactPartnerEntity
        : null;

    const revenueValue =
      opportunity.type === 'Opportunity'
        ? (opportunity.totalRevenue ?? 0) - (opportunity.totalExpenses ?? 0)
        : 0;
    const probabilityValue = opportunity.probability ?? 0;

    // Mantener el nombre canónico de la oportunidad separado del contacto.
    const originalOpportunityName = opportunity.name || '-';

    const customerCompany = getEntityDisplayName(opportunity.customerCompany);
    const customerCompanyId =
      opportunity.customerCompany && typeof opportunity.customerCompany === 'object'
        ? opportunity.customerCompany.id
        : null;

    // Client company comes from customerCompany first, then clientCompany/clientCompanyId.
    const clientCompany =
      opportunity.clientCompany != null && opportunity.clientCompany !== ''
        ? opportunity.clientCompany
        : null;
    // clientCompanyId is the client company Partner id. Prefer API value, then the contact parent.
    const clientCompanyId =
      opportunity.clientCompanyId != null
        ? opportunity.clientCompanyId
        : ((customerCompanyId ?? contactPartner?.parentId ?? null) as number | null);
    const clientCompanyPartner = clientCompanyId != null
      ? findPartner(Number(clientCompanyId))
      : null;
    const clientCompanyDisplay =
      customerCompany ||
      clientCompanyPartner?.name ||
      clientCompany ||
      '-';

    // Obtener ciudad
    let city = '-';
    if (opportunity.city) {
      city = opportunity.city;
    } else if (contactPartnerEntity && contactPartnerEntity.city) {
      city = contactPartnerEntity.city;
    }

    // Obtener país
    let country = '-';
    if (opportunity.countryId) {
      const countryObj = findCountry(opportunity.countryId);
      if (countryObj) {
        country = countryObj.name;
      }
    } else if (contactPartnerEntity && contactPartnerEntity.countryId) {
      const countryObj = findCountry(contactPartnerEntity.countryId);
      if (countryObj) {
        country = countryObj.name;
      }
    }

    // Obtener equipo de ventas
    let salesTeam = '-';
    if (opportunity.teamId) {
      const team = findSalesTeam(opportunity.teamId);
      if (team) {
        salesTeam = team.name;
      }
    }

    // Normalizar jobTitle: backend puede devolver job_title (snake_case) o jobTitle (camelCase)
    const jobTitle = opportunity.jobTitle ?? opportunity.job_title ?? null;

    return {
      ...opportunity,
      clientCompany: clientCompanyDisplay,
      clientCompanyId: opportunity.clientCompanyId ?? clientCompanyId,
      clientCompanyDisplay,
      // Mapear propiedades para la tabla
      oportunidad: originalOpportunityName,
      originalOpportunityName, // Guardar también aquí para fácil acceso
      name: originalOpportunityName,
      contactName,
      email: opportunity.email || '-',
      jobTitle,
      comercial: comercial,
      ingreso: revenueValue,
      mmr: revenueValue && probabilityValue
        ? (revenueValue * probabilityValue / 100)
        : 0, // MMR = valor financiero mostrado * probabilidad / 100
      etapa: etapa,
      city: city,
      country: country,
      salesTeam: salesTeam,
      // Guardar los objetos completos para mostrar avatares
      contactPartner: contactPartner,
      comercialUser: comercialUser,
      // Mantener el objeto original para acceso a propiedades anidadas
      originalStage: opportunity.stage, // Preservar el stage original para debugging
    };
  }

  /**
   * Agrupa las oportunidades según el campo activo
   */
  private groupOpportunities(opportunities: any[]): any[] {
    const criteria = this.getDisplayGroupingCriteria();

    if (!criteria.length) {
      return this.groupOpportunitiesByCompany(opportunities);
    }

    return this.groupOpportunitiesByCriteria(opportunities, criteria);
  }

  private getDisplayGroupingCriteria(): ActiveGroupingCriterion[] {
    const criteria = this.getActiveGroupingCriteria();

    if (!criteria.length) {
      return [];
    }

    return criteria.filter(
      (criterion, index, all) =>
        all.findIndex((item) => item.field === criterion.field) === index,
    );
  }

  private groupOpportunitiesByCriteria(
    opportunities: any[],
    criteria: ActiveGroupingCriterion[],
    level: number = 0,
    parentKey: string = 'opportunities',
  ): any[] {
    const criterion = criteria[level];

    if (!criterion) {
      return opportunities;
    }

    const groupMap = new Map<string | number, any[]>();

    if (criterion.field === 'stageId') {
      this.getSelectedStageGroupKeys(criterion).forEach((stageId) => {
        groupMap.set(stageId, []);
      });
    }

    opportunities.forEach((opportunity) => {
      const groupValue = this.getOpportunityGroupValue(opportunity, criterion.field);
      const groupKey = this.normalizeGroupKey(groupValue);

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }

      groupMap.get(groupKey)!.push(opportunity);
    });

    return Array.from(groupMap.entries()).map(([groupKey, groupedOpportunities]) => {
      const label = this.getOpportunityGroupLabel(criterion.field, groupKey, groupedOpportunities[0]);
      const groupId = `${parentKey}-${criterion.field}-${String(groupKey)}`;
      const childItems = groupedOpportunities.length
        ? this.groupOpportunitiesByCriteria(
          groupedOpportunities,
          criteria,
          level + 1,
          groupId,
        )
        : [];
      const ingreso = groupedOpportunities.reduce(
        (sum, opportunity) => sum + (Number(opportunity.ingreso) || 0),
        0,
      );
      const mmr = groupedOpportunities.reduce(
        (sum, opportunity) => sum + (Number(opportunity.mmr) || 0),
        0,
      );
      const groupName = `${label} (${groupedOpportunities.length})`;

      return {
        id: groupId,
        groupName,
        oportunidad: groupName,
        name: groupName,
        email: '-',
        comercial: '-',
        ingreso,
        mmr,
        etapa: criterion.field === 'stageId' ? label : '-',
        [criterion.field]: groupKey === '__none__' ? null : groupKey,
        items: childItems,
      };
    });
  }

  private getSelectedStageGroupKeys(criterion: ActiveGroupingCriterion): Array<string | number> {
    const selectedIds = criterion.values
      .map((value) => this.normalizeGroupKey(value))
      .filter((value): value is string | number => value !== '__none__');
    const selectedIdSet = new Set(selectedIds.map((value) => String(value)));
    const orderedStageIds = this.globalValuesService.stages$.value
      .filter((stage) => selectedIdSet.has(String(stage.id)))
      .sort((stageA, stageB) => (stageA.sequence ?? 0) - (stageB.sequence ?? 0))
      .map((stage) => stage.id);
    const orderedSet = new Set(orderedStageIds.map((value) => String(value)));
    const missingSelectedIds = selectedIds.filter(
      (value) => !orderedSet.has(String(value)),
    );

    return [...orderedStageIds, ...missingSelectedIds];
  }

  private normalizeGroupKey(value: any): string | number {
    if (value === undefined || value === null || value === '') {
      return '__none__';
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) && String(value).trim() !== ''
      ? numericValue
      : String(value);
  }

  private getOpportunityGroupValue(opportunity: any, field: string): string | number | null {
    const value = opportunity?.[field] ?? opportunity?.originalOpportunity?.[field];

    if (value === undefined || value === null || value === '') {
      return null;
    }

    return value;
  }

  private getOpportunityGroupLabel(
    field: string,
    value: string | number,
    sample: any,
  ): string {
    if (value === '__none__') {
      return 'Ninguno';
    }

    switch (field) {
      case 'userId': {
        const user = this.globalValuesService.users$.value.find((item) => item.id === Number(value));
        const name = user?.partner
          ? `${user.partner.name || ''} ${user.partner.surName || ''}`.trim()
          : '';
        return name || 'Ninguno';
      }
      case 'companyId': {
        const companies = this.authService.loggedUser$.value?.companies || [];
        const company = companies.find((item: any) => item.id === Number(value));
        return company?.name || sample?.company?.name || 'Ninguno';
      }
      case 'stageId': {
        const stage = this.globalValuesService.stages$.value.find((item) => item.id === Number(value));
        return stage?.name || sample?.etapa || 'Ninguno';
      }
      case 'countryId': {
        const country = this.globalValuesService.countries$.value.find((item) => item.id === Number(value));
        return country?.name || 'Ninguno';
      }
      case 'partnerId': {
        const partner =
          sample?.contactPartner ||
          this.globalValuesService.partnerlist.find((item) => item.id === Number(value));
        const name = partner ? `${partner.name || ''} ${partner.surName || ''}`.trim() : '';
        return name || sample?.contacto || 'Ninguno';
      }
      case 'teamId': {
        const team = this.globalValuesService.salesTeams$.value.find((item) => item.id === Number(value));
        return team?.name || 'Ninguno';
      }
      case 'lostReasonId': {
        const reason = this.globalValuesService.lossReasons$.value.find((item) => item.id === Number(value));
        return reason?.name || 'Ninguno';
      }
      case 'city':
        return String(value || sample?.city || 'Ninguno');
      default:
        return String(value || 'Ninguno');
    }
  }

  /**
   * Agrupa las oportunidades por compañía
   */
  private groupOpportunitiesByCompany(opportunities: any[]): any[] {
    const companyMap = new Map<number, any>();
    const loggedUser = this.authService.loggedUser$.value;
    const userCompanies = loggedUser?.companies || [];
    const selectedCompanyIds = this.companyService.selectedCompanyIds;

    // Solo pre-crear filas vacías cuando no hay filtro de agrupación activo por compañía
    // (si hay filtro activo, el backend ya filtra y no debemos mostrar compañías no solicitadas)
    if (!this.activeGroupByValues || this.activeGroupByValues.length === 0) {
      selectedCompanyIds.forEach(companyId => {
        const company = userCompanies.find(c => c.id === companyId);
        if (company && !companyMap.has(companyId)) {
          const companyName = company.name;
          companyMap.set(companyId, {
            id: companyId,
            oportunidad: this.formatCompanyGroupName(companyName, 0),
            name: this.formatCompanyGroupName(companyName, 0),
            email: '-',
            comercial: '-',
            ingreso: 0,
            mmr: 0,
            etapa: '-',
            companyId: companyId,
            company: company,
            companyName,
            items: []
          });
        }
      });
    }

    // Luego, llenar con las oportunidades existentes
    opportunities.forEach(opportunity => {
      const companyId = opportunity.companyId || 0;

      // Buscar el nombre de la compañía en las compañías del usuario logueado
      const company = userCompanies.find(c => c.id === companyId);
      const companyName = company?.name || opportunity.company?.name || `Compañía ${companyId}`;

      if (!companyMap.has(companyId)) {
        // Crear fila principal para la compañía (caso: compañía no estaba en selectedCompanyIds)
        companyMap.set(companyId, {
          id: companyId,
          oportunidad: this.formatCompanyGroupName(companyName, 0),
          name: this.formatCompanyGroupName(companyName, 0),
          email: '-',
          comercial: '-',
          ingreso: 0,
          mmr: 0,
          etapa: '-',
          companyId: companyId,
          company: company || opportunity.company,
          companyName,
          items: []
        });
      }

      const companyRow = companyMap.get(companyId)!;

      // Agregar la oportunidad al array de items
      companyRow.items.push(opportunity);

      // Sumar los ingresos y MMR de la compañía
      companyRow.ingreso += opportunity.ingreso || 0;
      companyRow.mmr += opportunity.mmr || 0;
    });

    return Array.from(companyMap.values()).map((companyRow) => {
      const companyName =
        companyRow.companyName ||
        companyRow.company?.name ||
        companyRow.oportunidad ||
        'Compañía';
      const companyLabel = this.formatCompanyGroupName(
        companyName,
        companyRow.items?.length || 0,
      );

      return {
        ...companyRow,
        oportunidad: companyLabel,
        name: companyLabel,
      };
    });
  }

  private formatCompanyGroupName(companyName: string, itemsCount: number): string {
    const normalizedName = companyName?.trim() || 'Compañía';
    return `${normalizedName} (${itemsCount})`;
  }
}

